import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  ExternalLink,
  Link2,
  Mail,
  MoreHorizontal,
  RefreshCw,
  Search as SearchIcon,
  SlidersHorizontal,
} from 'lucide-react';
import api from '../../lib/api';
import { jobUrlSegment, toSlug } from '../../lib/slug';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Badge from '../../components/ui/Badge';
import DataTable from '../../components/ui/DataTable';
import Modal from '../../components/ui/Modal';
import type { Invitation, Job } from '../../types';

type LastMessaging = {
  channel: string;
  status: string;
  sentAt: string | null;
  lastError: string | null;
  outboxCreatedAt: string;
} | null;

type InvitationRow = Invitation & {
  job: Job & { brandOrg?: { name?: string } };
  kolProfile: { id: string; name?: string; email?: string | null; userId?: string | null };
  lastMessaging?: LastMessaging;
  inviteUrl?: string | null;
};

type ListResponse = {
  items: InvitationRow[];
  total: number;
  skip: number;
  take: number;
};

type InvitationsDashboardProps = {
  role: 'AGENCY' | 'ADMIN';
  title: string;
  /** job list link for agency */
  jobsLink?: string;
};

const STATUS_OPTIONS = ['', 'PENDING', 'ACCEPTED', 'REJECTED', 'MODIFICATION_REQUESTED', 'WITHDRAWN'];

const STATUS_LABELS: Record<string, string> = {
  '': 'All',
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  MODIFICATION_REQUESTED: 'Changes',
  WITHDRAWN: 'Withdrawn',
};

function invitationStatusPill(status: string) {
  const s = status.toUpperCase();
  if (s === 'ACCEPTED') return 'status-pill status-pill-pos';
  if (['PENDING', 'MODIFICATION_REQUESTED'].includes(s)) return 'status-pill status-pill-warn';
  if (['REJECTED', 'WITHDRAWN'].includes(s)) return 'status-pill status-pill-danger';
  return 'status-pill status-pill-muted';
}

function compactDate(value?: string | null) {
  if (!value) return 'Not sent';
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export default function InvitationsDashboard({ role, title, jobsLink }: InvitationsDashboardProps) {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [jobId, setJobId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [skip, setSkip] = useState(0);
  const [take] = useState(50);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [queryVersion, setQueryVersion] = useState(0);
  const [jobs, setJobs] = useState<Pick<Job, 'id' | 'title'>[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mobileFilterDraft, setMobileFilterDraft] = useState({
    jobId: '',
    dateFrom: '',
    dateTo: '',
    onlyUnlinked: false,
  });
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [actionRow, setActionRow] = useState<InvitationRow | null>(null);
  const [statusTotals, setStatusTotals] = useState<Record<string, number>>({});
  const [filterPreviewTotal, setFilterPreviewTotal] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const { data: jobList } = await api.get<Job[]>('/jobs');
        if (!active) return;
        setJobs(jobList.map((j) => ({ id: j.id, title: j.title })));
      } catch {
        if (active) setJobs([]);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {
        paged: '1',
        includeMessaging: '1',
        skip: String(skip),
        take: String(take),
      };
      if (status) params.status = status;
      if (search.trim()) params.search = search.trim();
      if (onlyUnlinked) params.onlyUnlinkedKol = '1';
      if (jobId) params.jobId = jobId;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const { data: body } = await api.get<ListResponse>('/invitations', { params });
      setData(body);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load invitations');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [skip, take, queryVersion, role]);

  useEffect(() => {
    let active = true;
    const commonParams: Record<string, string> = {
      paged: '1',
      skip: '0',
      take: '1',
    };
    if (search.trim()) commonParams.search = search.trim();
    if (onlyUnlinked) commonParams.onlyUnlinkedKol = '1';
    if (jobId) commonParams.jobId = jobId;
    if (dateFrom) commonParams.dateFrom = dateFrom;
    if (dateTo) commonParams.dateTo = dateTo;

    void Promise.all(
      STATUS_OPTIONS.map(async (statusOption) => {
        const params = { ...commonParams };
        if (statusOption) params.status = statusOption;
        const response = await api.get<ListResponse>('/invitations', { params });
        return [statusOption, response.data.total] as const;
      })
    )
      .then((entries) => {
        if (active) setStatusTotals(Object.fromEntries(entries));
      })
      .catch(() => {
        if (active) setStatusTotals({});
      });

    return () => {
      active = false;
    };
  }, [queryVersion, role]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterPreviewTotal(null);
      return;
    }

    let active = true;
    setFilterPreviewTotal(null);
    const timeout = window.setTimeout(() => {
      const params: Record<string, string> = {
        paged: '1',
        skip: '0',
        take: '1',
      };
      if (status) params.status = status;
      if (search.trim()) params.search = search.trim();
      if (mobileFilterDraft.onlyUnlinked) params.onlyUnlinkedKol = '1';
      if (mobileFilterDraft.jobId) params.jobId = mobileFilterDraft.jobId;
      if (mobileFilterDraft.dateFrom) params.dateFrom = mobileFilterDraft.dateFrom;
      if (mobileFilterDraft.dateTo) params.dateTo = mobileFilterDraft.dateTo;

      void api
        .get<ListResponse>('/invitations', { params })
        .then(({ data: preview }) => {
          if (active) setFilterPreviewTotal(preview.total);
        })
        .catch(() => {
          if (active) setFilterPreviewTotal(null);
        });
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [
    filtersOpen,
    status,
    search,
    mobileFilterDraft.onlyUnlinked,
    mobileFilterDraft.jobId,
    mobileFilterDraft.dateFrom,
    mobileFilterDraft.dateTo,
    role,
  ]);

  const copyInviteLink = async (row: InvitationRow) => {
    const url = row.inviteUrl?.trim();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(row.id);
      window.setTimeout(() => setCopiedId((id) => (id === row.id ? null : id)), 2000);
    } catch {
      alert('Could not copy to clipboard');
    }
  };

  const resendInviteEmail = async (row: InvitationRow) => {
    try {
      setResendingId(row.id);
      await api.post(`/invitations/${encodeURIComponent(row.id)}/resend-invite-email`);
      await load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Resend failed');
    } finally {
      setResendingId(null);
    }
  };

  const invitationHref = (row: InvitationRow) =>
    `/${role === 'ADMIN' ? 'admin' : 'agency'}/invitations/${toSlug(
      row.kolProfile?.name || 'invitation',
      row.id
    )}`;

  const selectStatus = (nextStatus: string) => {
    setStatus(nextStatus);
    setSkip(0);
    setQueryVersion((version) => version + 1);
  };

  const columns = [
      {
        header: 'KOL',
        accessor: (row: InvitationRow) => (
          <div className="flex items-start gap-2">
            <span
              className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${
                row.kolProfile?.userId ? 'bg-emerald-500' : 'bg-zinc-400'
              }`}
              title={row.kolProfile?.userId ? 'Linked to app account' : 'Not linked to app account'}
            />
            <div>
              <div className="font-medium text-glass-text">{row.kolProfile?.name || '—'}</div>
              <div className="text-xs text-glass-text-3">{row.kolProfile?.email || '—'}</div>
              {!row.kolProfile?.userId && (
                <Badge variant="secondary" className="mt-1 text-[10px]">
                  Not linked
                </Badge>
              )}
            </div>
          </div>
        ),
      },
      {
        header: 'Campaign',
        accessor: (row: InvitationRow) =>
          role === 'ADMIN' ? (
            <Link to="/admin/campaigns" className="text-sm font-medium text-accent2 hover:underline">
              {row.job?.title || '—'}
            </Link>
          ) : (
            <Link
              to={`/agency/campaigns/${jobUrlSegment(row.job)}`}
              className="text-accent2 hover:underline text-sm font-medium"
            >
              {row.job?.title || '—'}
            </Link>
          ),
      },
      {
        header: 'Status',
        accessor: (row: InvitationRow) => (
          <span className={invitationStatusPill(row.status)}>{row.status}</span>
        ),
      },
      {
        header: 'Sent / responded',
        accessor: (row: InvitationRow) => (
          <div className="text-xs text-glass-text-2 space-y-0.5">
            <div>Sent: {row.broadcastedAt ? new Date(row.broadcastedAt).toLocaleString() : '—'}</div>
            <div>Updated: {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}</div>
          </div>
        ),
      },
      {
        header: 'Last messaging',
        accessor: (row: InvitationRow) => {
          const m = row.lastMessaging;
          if (!m) return <span className="text-xs text-glass-text-3">—</span>;
          return (
            <div className="text-xs space-y-0.5">
              <div>
                <span className="text-glass-text-2">{m.channel}</span>{' '}
                <Badge variant={m.status === 'SENT' ? 'success' : m.status === 'FAILED' ? 'destructive' : 'secondary'}>
                  {m.status}
                </Badge>
              </div>
              <div className="text-glass-text-3">{m.sentAt ? new Date(m.sentAt).toLocaleString() : 'Not sent'}</div>
              {m.lastError && <div className="text-amber-600 truncate max-w-[200px]" title={m.lastError}>{m.lastError}</div>}
            </div>
          );
        },
      },
      {
        header: '',
        accessor: (row: InvitationRow) => (
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-glass-border px-2 py-1 text-xs text-glass-text-2 hover:bg-glass-muted disabled:opacity-50"
              disabled={!row.inviteUrl}
              title="Copy invite link"
              onClick={() => void copyInviteLink(row)}
            >
              <Link2 className="h-3.5 w-3.5" />
              {copiedId === row.id ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-glass-border px-2 py-1 text-xs text-glass-text-2 hover:bg-glass-muted disabled:opacity-50"
              title="Resend invitation email"
              disabled={resendingId === row.id}
              onClick={() => void resendInviteEmail(row)}
            >
              <Mail className="h-3.5 w-3.5" />
              {resendingId === row.id ? '…' : 'Email'}
            </button>
            <Link
              to={invitationHref(row)}
              className="text-xs text-accent2 hover:underline whitespace-nowrap"
            >
              Open
            </Link>
          </div>
        ),
      },
    ];

  const applyFilters = () => {
    setSkip(0);
    setQueryVersion((v) => v + 1);
  };

  const openMobileFilters = () => {
    setMobileFilterDraft({ jobId, dateFrom, dateTo, onlyUnlinked });
    setFiltersOpen(true);
  };

  const applyMobileFilters = () => {
    setJobId(mobileFilterDraft.jobId);
    setDateFrom(mobileFilterDraft.dateFrom);
    setDateTo(mobileFilterDraft.dateTo);
    setOnlyUnlinked(mobileFilterDraft.onlyUnlinked);
    setSkip(0);
    setQueryVersion((version) => version + 1);
    setFiltersOpen(false);
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (search.trim()) params.set('search', search.trim());
      if (onlyUnlinked) params.set('onlyUnlinkedKol', '1');
      if (jobId) params.set('jobId', jobId);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const res = await api.get(`/invitations/export.csv?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'invitations.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Export failed');
    }
  };

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const canPrev = skip > 0;
  const canNext = skip + take < total;
  const mobileFilterCount = [jobId, dateFrom, dateTo, onlyUnlinked ? 'unlinked' : ''].filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[1.375rem] font-semibold tracking-tightest text-glass-text md:text-[2.25rem]">{title}</h1>
          <p className="mt-1 text-xs text-glass-text-2 md:text-sm">
            All invitations across your campaigns with invitation status and recent messaging-outbox events.
            {jobsLink && (
              <span className="hidden md:inline">
                {' '}
                <Link to={jobsLink} className="text-accent2 hover:underline">
                  Back to jobs
                </Link>
              </span>
            )}
          </p>
        </div>
        <div className="hidden flex-wrap gap-2 md:flex">
          <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => void exportCsv()}>
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-11 w-11 shrink-0 md:hidden"
          onClick={() => setHeaderMenuOpen(true)}
          aria-label="Invitation page actions"
        >
          <MoreHorizontal className="h-5 w-5" />
        </Button>
      </div>

      <div className="space-y-3 md:hidden">
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {STATUS_OPTIONS.map((option) => {
            const count = statusTotals[option];
            const active = status === option;
            return (
              <button
                key={option || 'ALL'}
                type="button"
                onClick={() => selectStatus(option)}
                className={`min-h-11 shrink-0 cursor-pointer rounded-full border px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 ${
                  active
                    ? 'border-accent2/40 bg-accent2/15 font-semibold text-accentText'
                    : 'border-glass bg-glass-2 font-medium text-glass-text-2 hover:bg-glass-3'
                }`}
                aria-pressed={active}
              >
                {STATUS_LABELS[option]}{typeof count === 'number' ? ` ${count}` : ''}
              </button>
            );
          })}
        </div>

        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            applyFilters();
          }}
        >
          <div className="relative min-w-0 flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-glass-text-3" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name or email"
              aria-label="Search invitations"
              className="h-11 pl-9"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="relative h-11 w-11 shrink-0 border-accent2/30 bg-accent2/10 text-accentText"
            onClick={openMobileFilters}
            aria-label={`Open invitation filters${mobileFilterCount ? `, ${mobileFilterCount} active` : ''}`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {mobileFilterCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent2 px-1 text-[10px] font-bold text-white">
                {mobileFilterCount}
              </span>
            )}
          </Button>
        </form>
      </div>

      <div className="hidden glass-card p-4 md:flex md:flex-wrap md:items-end md:gap-3">
        <div className="min-w-[140px]">
          <label className="block text-xs text-glass-text-3 mb-1">Status</label>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s || 'ALL'} value={s}>
                {s || 'All statuses'}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-w-[200px] max-w-[280px]">
          <label className="block text-xs text-glass-text-3 mb-1">Campaign</label>
          <Select value={jobId} onChange={(e) => setJobId(e.target.value)}>
            <option value="">All campaigns</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.title}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-w-[130px]">
          <label className="block text-xs text-glass-text-3 mb-1">From date</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="min-w-[130px]">
          <label className="block text-xs text-glass-text-3 mb-1">To date</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-glass-text-3 mb-1">Search name or email</label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter…" />
        </div>
        <label className="flex items-center gap-2 text-sm text-glass-text-2 pb-2">
          <input type="checkbox" checked={onlyUnlinked} onChange={(e) => setOnlyUnlinked(e.target.checked)} />
          Not linked to user only
        </label>
        <Button type="button" size="sm" onClick={applyFilters}>
          Apply
        </Button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</div>}

      <div className="glass-card overflow-hidden md:hidden">
        {loading && rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-glass-text-3">Loading invitations…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-glass-text-3">
            No invitations match your filters.
          </div>
        ) : (
          <div className="divide-y divide-glass">
            {rows.map((row) => {
              const messaging = row.lastMessaging;
              return (
                <div key={row.id} className="flex min-h-[78px] items-center gap-2 px-[13px] py-2.5">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      row.kolProfile?.userId ? 'bg-emerald-500' : 'bg-zinc-400'
                    }`}
                    title={row.kolProfile?.userId ? 'Linked to app account' : 'Not linked to app account'}
                  />
                  <Link to={invitationHref(row)} className="min-w-0 flex-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm font-semibold text-glass-text">
                        {row.kolProfile?.name || 'Unnamed KOL'}
                      </span>
                      <span className={`${invitationStatusPill(row.status)} shrink-0 text-[10px]`}>
                        {STATUS_LABELS[row.status?.toUpperCase()] || row.status}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-glass-text-3">
                      <span className="text-accentText">{row.job?.title || 'Untitled campaign'}</span>
                      {' · '}
                      {compactDate(row.broadcastedAt)}
                    </div>
                    <div className="mt-0.5 truncate text-[11px] text-glass-text-3">
                      {messaging ? `${messaging.channel} · ${messaging.status}` : 'No messaging event'}
                    </div>
                  </Link>
                  <button
                    type="button"
                    className="inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-glass-text-2 transition-colors hover:bg-glass-3 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50"
                    onClick={() => setActionRow(row)}
                    aria-label={`Actions for ${row.kolProfile?.name || 'invitation'}`}
                  >
                    <MoreHorizontal className="h-5 w-5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="space-y-2 border-t border-glass px-[13px] py-3 text-xs text-glass-text-2">
          <div>
            Showing {rows.length ? skip + 1 : 0}–{skip + rows.length} of {total}
          </div>
          {(canPrev || canNext) && (
            <div className="flex gap-2">
              {canPrev && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 flex-1"
                  disabled={loading}
                  onClick={() => setSkip((current) => Math.max(0, current - take))}
                >
                  Previous 50
                </Button>
              )}
              {canNext && (
                <Button
                  type="button"
                  className="h-11 flex-1"
                  disabled={loading}
                  onClick={() => setSkip((current) => current + take)}
                >
                  Load next 50
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="hidden glass-card overflow-hidden md:block">
        <DataTable
          data={rows}
          columns={columns}
          isLoading={loading}
          emptyMessage="No invitations match your filters."
        />
        <div className="flex items-center justify-between px-4 py-3 border-t border-glass text-sm text-glass-text-2">
          <span>
            Showing {rows.length ? skip + 1 : 0}–{skip + rows.length} of {total}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" disabled={!canPrev || loading} onClick={() => setSkip((s) => Math.max(0, s - take))}>
              Previous
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={!canNext || loading} onClick={() => setSkip((s) => s + take)}>
              Next
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Invitation filters"
        size="sm"
        mobilePresentation="sheet"
        footer={
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-11 flex-1"
              onClick={() => {
                setMobileFilterDraft({
                  jobId: '',
                  dateFrom: '',
                  dateTo: '',
                  onlyUnlinked: false,
                });
              }}
            >
              Clear
            </Button>
            <Button
              type="button"
              className="h-11 flex-[2]"
              onClick={applyMobileFilters}
            >
              {filterPreviewTotal === null
                ? 'Show invitations'
                : `Show ${filterPreviewTotal.toLocaleString()} invitations`}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-glass-text-3" htmlFor="mobile-invitation-campaign">
              Campaign
            </label>
            <Select
              id="mobile-invitation-campaign"
              value={mobileFilterDraft.jobId}
              onChange={(event) =>
                setMobileFilterDraft((current) => ({ ...current, jobId: event.target.value }))
              }
              className="h-11"
            >
              <option value="">All campaigns</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-glass-text-3" htmlFor="mobile-invitation-from">
                From date
              </label>
              <Input
                id="mobile-invitation-from"
                type="date"
                value={mobileFilterDraft.dateFrom}
                onChange={(event) =>
                  setMobileFilterDraft((current) => ({ ...current, dateFrom: event.target.value }))
                }
                className="h-11"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-glass-text-3" htmlFor="mobile-invitation-to">
                To date
              </label>
              <Input
                id="mobile-invitation-to"
                type="date"
                value={mobileFilterDraft.dateTo}
                onChange={(event) =>
                  setMobileFilterDraft((current) => ({ ...current, dateTo: event.target.value }))
                }
                className="h-11"
              />
            </div>
          </div>
          <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border border-glass bg-glass-2 px-3 text-sm text-glass-text-2">
            <input
              type="checkbox"
              checked={mobileFilterDraft.onlyUnlinked}
              onChange={(event) =>
                setMobileFilterDraft((current) => ({
                  ...current,
                  onlyUnlinked: event.target.checked,
                }))
              }
              className="h-4 w-4 accent-accent2"
            />
            Not linked to user only
          </label>
        </div>
      </Modal>

      <Modal
        isOpen={headerMenuOpen}
        onClose={() => setHeaderMenuOpen(false)}
        title="Invitation actions"
        size="sm"
        mobilePresentation="sheet"
      >
        <div className="space-y-1">
          <button
            type="button"
            className="flex h-11 w-full cursor-pointer items-center gap-3 rounded-lg px-3 text-left text-sm text-glass-text transition-colors hover:bg-glass-3"
            onClick={() => {
              setHeaderMenuOpen(false);
              void load();
            }}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh invitations
          </button>
          <button
            type="button"
            className="flex h-11 w-full cursor-pointer items-center gap-3 rounded-lg px-3 text-left text-sm text-glass-text transition-colors hover:bg-glass-3"
            onClick={() => {
              setHeaderMenuOpen(false);
              void exportCsv();
            }}
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={!!actionRow}
        onClose={() => setActionRow(null)}
        title={actionRow ? `Actions · ${actionRow.kolProfile?.name || 'Invitation'}` : 'Invitation actions'}
        size="sm"
        mobilePresentation="sheet"
      >
        {actionRow && (
          <div className="space-y-1">
            <button
              type="button"
              className="flex h-11 w-full cursor-pointer items-center gap-3 rounded-lg px-3 text-left text-sm text-glass-text transition-colors hover:bg-glass-3 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!actionRow.inviteUrl}
              onClick={() => void copyInviteLink(actionRow)}
            >
              <Link2 className="h-4 w-4" />
              {copiedId === actionRow.id ? 'Link copied' : 'Copy invite link'}
            </button>
            <button
              type="button"
              className="flex h-11 w-full cursor-pointer items-center gap-3 rounded-lg px-3 text-left text-sm text-glass-text transition-colors hover:bg-glass-3 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={resendingId === actionRow.id}
              onClick={() => void resendInviteEmail(actionRow)}
            >
              <Mail className="h-4 w-4" />
              {resendingId === actionRow.id ? 'Resending…' : 'Resend invitation email'}
            </button>
            <Link
              to={invitationHref(actionRow)}
              className="flex h-11 items-center gap-3 rounded-lg px-3 text-sm text-glass-text transition-colors hover:bg-glass-3"
              onClick={() => setActionRow(null)}
            >
              <ExternalLink className="h-4 w-4" />
              Open invitation
            </Link>
          </div>
        )}
      </Modal>
    </div>
  );
}
